import React from 'react';
import {
  Box,
  Typography,
  Tooltip,
  useTheme,
  TextField,
  CircularProgress,
  IconButton,
} from '@mui/material';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditRounded from '@mui/icons-material/EditRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { config, buildApiUrl, tenantApiPrefix } from '../../../config';
import { getTenantIdFromStorage } from '../../../utils/onboardingLocalStorage';
import { SnackbarContext } from '../../../ui/SnackbarProvider';
import type { KnowledgePillItem } from '../stores/useCopilotStore';

const TYPE_LABEL: Record<string, string> = {
  metric: 'metric',
  business_rule: 'rule',
  term: 'term',
};

const INFO_TOOLTIP = `${config.brandName} automatically learns your preferences and saves them to your Knowledge Base. These definitions will be applied in future analyses.`;

interface KnowledgePillProps {
  items: KnowledgePillItem[];
}

function authHeaders(): Record<string, string> {
  const token = window.localStorage.getItem('klaris_jwt');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/** Resolve node_id for older thread history that predates the field. */
async function resolveNodeId(item: KnowledgePillItem): Promise<string | null> {
  if (item.node_id) return item.node_id;
  const tid = getTenantIdFromStorage();
  if (!tid || !item.name) return null;
  const params = new URLSearchParams({
    q: item.name,
    node_type: item.node_type,
    limit: '10',
  });
  const resp = await fetch(
    buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes?${params}`),
    { headers: authHeaders() },
  );
  if (!resp.ok) return null;
  const data = await resp.json();
  const nodes: Array<{ node_id: string; name: string; node_type: string }> = data.nodes || [];
  const exact = nodes.find(
    (n) =>
      n.node_type === item.node_type &&
      n.name.trim().toLowerCase() === item.name.trim().toLowerCase(),
  );
  return exact?.node_id || nodes[0]?.node_id || null;
}

export default function KnowledgePill({ items }: KnowledgePillProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (!items || items.length === 0) return null;

  const borderColor = isDark ? '#2d3748' : '#e2e8f0';
  const bgColor = isDark ? '#1a1f2e' : '#ffffff';

  return (
    <Box
      sx={{
        my: 0.75,
        width: '100%',
        borderRadius: '10px',
        border: '1px solid',
        borderColor,
        bgcolor: bgColor,
        overflow: 'hidden',
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
          boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
        },
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.9,
          borderBottom: '1px solid',
          borderColor,
          gap: 0.5,
        }}
      >
        <AutoAwesomeRounded sx={{ fontSize: 14, color: isDark ? '#818cf8' : '#6366f1', flexShrink: 0 }} />
        <Typography
          sx={{ fontSize: '0.8rem', fontWeight: 700, color: isDark ? '#e2e8f0' : '#111827', flex: 1, lineHeight: 1 }}
        >
          Knowledge base was updated
        </Typography>
        <Tooltip title={INFO_TOOLTIP} placement="top" arrow>
          <InfoOutlinedIcon
            sx={{ fontSize: 14, color: isDark ? '#64748b' : '#9ca3af', cursor: 'default', flexShrink: 0 }}
          />
        </Tooltip>
      </Box>

      {items.map((item, i) => (
        <ItemRow
          key={item.node_id || `${item.name}-${i}`}
          item={item}
          isDark={isDark}
          borderColor={borderColor}
          isLast={i === items.length - 1}
        />
      ))}
    </Box>
  );
}

function ItemRow({
  item,
  isDark,
  borderColor,
  isLast,
}: {
  item: KnowledgePillItem;
  isDark: boolean;
  borderColor: string;
  isLast: boolean;
}) {
  const { notify } = React.useContext(SnackbarContext);
  const [display, setDisplay] = React.useState(item);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [editName, setEditName] = React.useState(item.name);
  const [editDefinition, setEditDefinition] = React.useState(item.definition || '');
  const [editSql, setEditSql] = React.useState(item.sql || '');
  const [saving, setSaving] = React.useState(false);
  const [resolving, setResolving] = React.useState(false);

  React.useEffect(() => {
    setDisplay(item);
  }, [item]);

  const typeLabel = TYPE_LABEL[display.node_type] || display.node_type;
  const hasDetail = !!(display.definition || display.sql);
  const muted = isDark ? '#64748b' : '#9ca3af';
  const accent = isDark ? '#818cf8' : '#6366f1';

  const startEdit = async () => {
    setEditName(display.name);
    setEditDefinition(display.definition || '');
    setEditSql(display.sql || '');
    setOpen(true);
    setEditing(true);

    if (!display.node_id) {
      setResolving(true);
      try {
        const id = await resolveNodeId(display);
        if (id) {
          setDisplay((d) => ({ ...d, node_id: id }));
        } else {
          notify('Could not find this definition in the knowledge base', 'error');
          setEditing(false);
        }
      } catch {
        notify('Failed to load definition for editing', 'error');
        setEditing(false);
      } finally {
        setResolving(false);
      }
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditName(display.name);
    setEditDefinition(display.definition || '');
    setEditSql(display.sql || '');
  };

  const saveEdit = async () => {
    const nodeId = display.node_id;
    if (!nodeId) {
      notify('Missing knowledge node id', 'error');
      return;
    }
    if (!editName.trim() || !editDefinition.trim()) {
      notify('Name and definition are required', 'error');
      return;
    }

    const tid = getTenantIdFromStorage();
    if (!tid) {
      notify('Not authenticated', 'error');
      return;
    }

    setSaving(true);
    try {
      const body: Record<string, string> = {};
      if (editName.trim() !== display.name) body.name = editName.trim();
      if (editDefinition.trim() !== (display.definition || '')) {
        body.definition = editDefinition.trim();
      }
      if (editSql.trim() !== (display.sql || '')) {
        body.sql = editSql.trim();
      }

      if (Object.keys(body).length === 0) {
        setEditing(false);
        return;
      }

      const resp = await fetch(
        buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes/${nodeId}`),
        {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(body),
        },
      );
      if (!resp.ok) throw new Error('Update failed');

      setDisplay({
        ...display,
        name: editName.trim(),
        definition: editDefinition.trim(),
        sql: editSql.trim() || undefined,
        node_id: nodeId,
      });
      setEditing(false);
      notify('Definition updated', 'success');
    } catch {
      notify('Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const iconBtnSx = {
    p: 0.35,
    color: muted,
    '&:hover': { color: accent, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' },
  };

  return (
    <Box sx={{ borderBottom: isLast ? 'none' : '1px solid', borderColor }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.7,
          gap: 0.5,
        }}
      >
        <Typography
          sx={(t) => ({
            fontSize: '0.8125rem',
            color: t.tokens.text.secondary,
            flex: 1,
            lineHeight: 1.4,
            minWidth: 0,
          })}
        >
          Saved{' '}
          <Typography
            component="span"
            sx={(t) => ({ fontWeight: 600, color: t.tokens.text.primary, fontSize: '0.8125rem' })}
          >
            {display.name}
          </Typography>{' '}
          <Typography component="span" sx={(t) => ({ fontSize: '0.75rem', color: t.tokens.text.tertiary })}>
            ({typeLabel})
          </Typography>{' '}
          to knowledge base
        </Typography>

        {!editing && (
          <Tooltip title="Edit" placement="top">
            <IconButton
              size="small"
              onClick={startEdit}
              disabled={resolving}
              aria-label="Edit knowledge definition"
              sx={iconBtnSx}
            >
              {resolving ? (
                <CircularProgress size={14} thickness={5} />
              ) : (
                <EditRounded sx={{ fontSize: 15 }} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {!editing && hasDetail && (
          <Box
            onClick={() => setOpen((v) => !v)}
            sx={{
              cursor: 'pointer',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              color: muted,
              borderRadius: 1,
              px: 0.5,
              py: 0.15,
              '&:hover': {
                color: accent,
                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              },
              transition: 'all 0.12s ease',
            }}
          >
            {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          </Box>
        )}
      </Box>

      {/* Expanded detail / edit form */}
      {(open || editing) && (
        <Box
          sx={{
            mx: 1.5,
            mb: 0.75,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb',
            border: '1px solid',
            borderColor,
          }}
        >
          {editing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                size="small"
                label="Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                fullWidth
                disabled={saving || resolving}
                autoFocus
              />
              <TextField
                size="small"
                label="Definition"
                value={editDefinition}
                onChange={(e) => setEditDefinition(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                disabled={saving || resolving}
              />
              <TextField
                size="small"
                label={display.node_type === 'business_rule' ? 'SQL condition' : 'SQL formula'}
                value={editSql}
                onChange={(e) => setEditSql(e.target.value)}
                fullWidth
                multiline
                minRows={1}
                disabled={saving || resolving}
                InputProps={{
                  sx: (t) => ({
                    fontFamily: t.typography.fontFamilyMono,
                    fontSize: '0.75rem',
                  }),
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <Tooltip title="Cancel">
                  <span>
                    <IconButton
                      size="small"
                      onClick={cancelEdit}
                      disabled={saving}
                      aria-label="Cancel edit"
                      sx={iconBtnSx}
                    >
                      <CloseRounded sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Save">
                  <span>
                    <IconButton
                      size="small"
                      onClick={saveEdit}
                      disabled={saving || resolving || !display.node_id}
                      aria-label="Save definition"
                      sx={{
                        ...iconBtnSx,
                        color: accent,
                        '&:hover': {
                          color: accent,
                          bgcolor: isDark ? 'rgba(129,140,248,0.12)' : 'rgba(99,102,241,0.08)',
                        },
                      }}
                    >
                      {saving ? (
                        <CircularProgress size={14} thickness={5} />
                      ) : (
                        <CheckRounded sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          ) : (
            <>
              {display.definition && (
                <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#cbd5e1' : '#374151', lineHeight: 1.6 }}>
                  {display.definition}
                </Typography>
              )}
              {display.sql && (
                <Box
                  sx={(t) => ({
                    mt: display.definition ? 0.5 : 0,
                    px: 0.75,
                    py: 0.4,
                    borderRadius: `${t.ds.radius.xs}px`,
                    bgcolor: t.tokens.surface.canvas,
                    fontFamily: t.typography.fontFamilyMono,
                    fontSize: '0.75rem',
                    color: t.tokens.brand.primary,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  })}
                >
                  {display.sql}
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
